import React from 'react';
import { AlertCircleIcon } from './icons/LucideIcons';
import { useAppearance } from '../contexts/AppearanceContext';

/**
 * /signup renders outside AppearanceProvider; there the appearance index.html
 * applied to <html> before first paint (device key or ?appearance=) is the truth.
 */
const useIsFable = () => {
  const { appearance, outsideProvider } = useAppearance();
  if (outsideProvider) return document.documentElement.getAttribute('data-appearance') === 'fable';
  return appearance === 'fable';
};

/** Fable's soft decorative circles. Pure decoration: hidden from AT, no hits. */
const Circle = ({ className }) => (
  <div aria-hidden="true" data-testid="auth-circle" className={`pointer-events-none absolute rounded-full ${className}`} />
);

/**
 * Pre-login screens (sign-in, forgot / reset password). Two columns from 900px:
 * a green brand panel and the form column; one column below that.
 *
 * Pre-authentication, so the panel shows no shop data: no shop is known before
 * login (the host does not identify one) and order volume must not leak.
 */
const AuthLayout = ({ children }) => {
  const isFable = useIsFable();
  const today = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
  if (isFable) return <FableAuthLayout today={today}>{children}</FableAuthLayout>;
  return (
    <div className="grid min-h-screen bg-surface-alt text-sm text-gray-900 min-[900px]:grid-cols-2">
      <div data-testid="auth-panel" className="hidden flex-col justify-between bg-floral-700 px-12 py-10 text-white min-[900px]:flex">
        <div className="flex items-center gap-2.5">
          <div className="grid h-7 w-7 place-items-center rounded-md bg-white/20 text-sm font-bold" aria-hidden="true">B</div>
          <span className="text-base font-semibold tracking-[-0.01em]">Bouqmark</span>
        </div>
        <div className="max-w-[40ch]">
          <div className="mb-3.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-white/70 [html[data-scheme=dark]:not([data-appearance=fable])_&]:text-white">{today}</div>
          <p className="text-[32px] font-semibold leading-[1.15] tracking-[-0.02em]">
            Every order, route and card message in one place.
          </p>
        </div>
        <div aria-hidden="true" />
      </div>
      <div className="flex flex-col items-center justify-center gap-6 px-6 py-10">
        {children}
        <p className="text-xs text-gray-600">© {new Date().getFullYear()} Bouqmark</p>
      </div>
    </div>
  );
};

/**
 * Fable: the same split, but the panel is the sand surface with a sage and a
 * terracotta circle, ink text and a Marcellus sentence. Below 900px the panel
 * goes (as in Forest) and smaller circles sit behind the card instead.
 */
const FableAuthLayout = ({ today, children }) => (
  <div className="grid min-h-screen bg-surface-alt text-sm text-gray-900 min-[900px]:grid-cols-2">
    <div data-testid="auth-panel" className="relative isolate hidden flex-col justify-between overflow-hidden bg-surface px-12 py-10 text-gray-900 min-[900px]:flex">
      <Circle className="-right-28 -top-28 -z-10 h-[380px] w-[380px] bg-accent2-200" />
      <Circle className="-bottom-24 -left-24 -z-10 h-[300px] w-[300px] bg-floral-200" />
      <div className="flex items-center gap-2.5">
        <div className="grid h-7 w-7 place-items-center rounded-full bg-floral-700 text-sm font-bold text-white" aria-hidden="true">B</div>
        <span className="text-base font-semibold tracking-[-0.01em]">Bouqmark</span>
      </div>
      <div className="max-w-[40ch]">
        <div className="mb-3.5 text-[13px] text-gray-700">{today}</div>
        <p className="font-heading text-[34px] font-normal leading-[1.2]">
          Every order, route and card message in one place.
        </p>
      </div>
      <div aria-hidden="true" />
    </div>
    <div className="relative isolate flex flex-col items-center justify-center gap-6 overflow-hidden px-6 py-10">
      <Circle className="-right-24 -top-24 -z-10 h-[240px] w-[240px] bg-accent2-200 min-[900px]:hidden" />
      <Circle className="-bottom-20 -left-20 -z-10 h-[200px] w-[200px] bg-floral-200 min-[900px]:hidden" />
      {children}
      <p className="text-xs text-gray-600">© {new Date().getFullYear()} Bouqmark</p>
    </div>
  </div>
);

/** Text link on the card. Forest dark uses floral-900 (the link token); floral-700 is only 3.8:1 there. */
export const AUTH_LINK = 'font-medium text-floral-700 hover:text-floral-800 hover:underline [html[data-scheme=dark]:not([data-appearance=fable])_&]:text-floral-900';

/**
 * The 400px form card every auth view sits in. Forest: hairline, no shadow.
 * Fable: sand surface, 28px radius, soft shadow; `.auth-card` scopes the pill
 * fields, segmented control and buttons in appearance.css.
 */
export const AuthCard = ({ children, as: Tag = 'div', ...rest }) => {
  const isFable = useIsFable();
  return (
    <Tag
      className={isFable
        ? 'auth-card flex w-full max-w-[400px] flex-col gap-[18px] rounded-[28px] bg-surface p-8 shadow-md'
        : 'flex w-full max-w-[400px] flex-col gap-[18px] rounded-2xl border border-gray-200 bg-white px-8 pb-7 pt-8'}
      {...rest}
    >
      {children}
    </Tag>
  );
};

/** Title + one-line sub at the top of a card. */
export const AuthHeading = ({ title, sub }) => {
  const isFable = useIsFable();
  return (
    <div>
      <h1 className={isFable
        ? 'font-heading text-[26px] font-normal leading-[1.2] text-gray-900'
        : 'text-2xl font-semibold tracking-[-0.02em] text-gray-900'}
      >
        {title}
      </h1>
      {sub && <p className="mt-1 text-gray-600">{sub}</p>}
    </div>
  );
};

/** Error line in the form; danger banner. */
export const AuthError = ({ children }) => (
  <div className="banner banner-danger" role="alert">
    <AlertCircleIcon size={16} className="shrink-0" />
    <span>{children}</span>
  </div>
);

export default AuthLayout;
